// src/components/common/WorkFromHomeModal.js
import React, { useState } from 'react';
import { Calendar, Home, X, MapPin, AlertCircle } from 'lucide-react';
import attendanceService from '../../services/attendance.service';

const getTodayDate = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
}).format(new Date()).replace(/\//g, '-');

const WorkFromHomeModal = ({ isOpen, onClose, managerId, onSubmitSuccess, prefilledFromDate = '', prefilledToDate = '' }) => {
    const [fromDate, setFromDate] = useState(prefilledFromDate || getTodayDate());
    const [toDate, setToDate] = useState(prefilledToDate || getTodayDate());
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!fromDate || !toDate || !reason.trim()) {
            setError('Please fill in all required fields');
            return;
        }

        // Validate date range
        if (new Date(toDate) < new Date(fromDate)) {
            setError('To Date must be on or after From Date');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const requestData = {
                fromDate: fromDate,
                toDate: toDate,
                reason: reason.trim(),
                managerId: managerId
            };

            console.log('🔵 Submitting WFH request with data:', requestData);
            console.log('🔵 Date range:', fromDate, 'to', toDate);
            console.log('🔵 Manager ID:', managerId);

            const response = await attendanceService.requestWorkFromHome(requestData);
            console.log('✅ WFH request response:', response);

            if (response.success) {
                onSubmitSuccess && onSubmitSuccess();
                onClose();
                alert('Work from home request submitted successfully!');
            } else {
                console.error('❌ WFH request failed:', response);
                const errorMsg = response.error || response.message || 'Failed to submit work from home request';
                setError(errorMsg);
                alert('Error: ' + errorMsg);
            }
        } catch (error) {
            console.error('❌ Error submitting WFH request:', error);
            console.error('❌ Error response:', error.response);
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to submit work from home request';
            setError(errorMsg);
            alert('Error: ' + errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setError('');
            setReason('');
            setFromDate(getTodayDate());
            setToDate(getTodayDate());
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <Home className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Work From Home Request
                        </h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        disabled={loading}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                            <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
                        </div>
                    )}

                    {/* Date Selection - From and To */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                <Calendar className="w-4 h-4 inline mr-2" />
                                From Date
                            </label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => {
                                    setFromDate(e.target.value);
                                    // If to date is before new from date, update it
                                    if (new Date(e.target.value) > new Date(toDate)) {
                                        setToDate(e.target.value);
                                    }
                                }}
                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                <Calendar className="w-4 h-4 inline mr-2" />
                                To Date
                            </label>
                            <input
                                type="date"
                                value={toDate}
                                min={fromDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>
                    </div>

                    {/* Reason */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Reason
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Please provide a reason for your work from home request..."
                            rows={4}
                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                            required
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {reason.length}/200 characters
                        </p>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                    Work From Home Guidelines
                                </p>
                                <ul className="text-xs text-blue-700 dark:text-blue-400 mt-2 space-y-1">
                                    <li>• Ensure you have proper internet connectivity</li>
                                    <li>• Be available during working hours</li>
                                    <li>• Regular check-ins with your manager</li>
                                    <li>• Submit request at least 24 hours in advance</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Home className="w-4 h-4" />
                                    Submit Request
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WorkFromHomeModal;
